import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { API_BASE_URL } from '../services/index';
import './SubscriptionWindowModal.css';

/**
 * SubscriptionWindowModal — INVEXIS admin only.
 *
 * Moves a customer business's access window to an EXACT one-month period.
 * The admin picks only the START date on the calendar; the END date is shown
 * but locked (start + 1 calendar month) and is recomputed server-side, so the
 * saved range can never be wrong. Every change is written to Businesses and
 * to SubscriptionWindowLog by PUT /api/subscription-window/:businessId.
 */

const DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// ---- date helpers (all on 'YYYY-MM-DD' strings, timezone-free) ------------
const pad = (n) => String(n).padStart(2, '0');
const iso = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;                  // m is 1-based
const parts = (s) => s.split('-').map(Number);                       // [y, m, d]
const daysInMonth = (y, m) => new Date(y, m, 0).getDate();           // m 1-based

/** Mirrors backend addOneMonth(): clamp to last day of the target month. */
export function addOneMonth(isoDate) {
  const [y, m, d] = parts(isoDate);
  const ny = m === 12 ? y + 1 : y;
  const nm = m === 12 ? 1 : m + 1;
  return iso(ny, nm, Math.min(d, daysInMonth(ny, nm)));
}

/**
 * Same bounds the backend enforces (3 months back, 12 months forward).
 * The API rejects a start more than 3 whole months back (monthsBetween < -3).
 * Day `d` of the month three months ago is the earliest allowed start; when
 * that month is too short to have day `d` (e.g. today = 31 May -> February),
 * nothing in it qualifies and the earliest allowed day is the 1st of the next.
 * The API rejects a start 12 or more whole months ahead (monthsBetween >= 12),
 * so the last selectable day is the day BEFORE the same date next year.
 * Keeping these in step is what makes edge case #5 true: the calendar never
 * offers a day the server would answer with 400.
 */
export function startDateBounds(today) {
  const [y, m, d] = parts(today);
  let pm = m - 3, py = y;
  if (pm < 1) { pm += 12; py -= 1; }
  let minDate;
  if (d <= daysInMonth(py, pm)) minDate = iso(py, pm, d);
  else minDate = iso(pm === 12 ? py + 1 : py, pm === 12 ? 1 : pm + 1, 1);
  let maxDate;
  if (d > 1) maxDate = iso(y + 1, m, d - 1);
  else {
    const mm = m === 1 ? 12 : m - 1;
    const my = m === 1 ? y : y + 1;
    maxDate = iso(my, mm, daysInMonth(my, mm));
  }
  return { minDate, maxDate };
}

export const todayLocalISO = () => {
  const n = new Date();
  return iso(n.getFullYear(), n.getMonth() + 1, n.getDate());
};

export const fmt = (isoDate) => {
  if (!isoDate) return '—';
  const [y, m, d] = parts(String(isoDate).slice(0, 10));
  return `${d} ${MONTHS[m - 1].slice(0, 3)} ${y}`;
};

export const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

// ---- Calendar ---------------------------------------------------------------
export function MonthCalendar({ start, end, onPickStart, minDate, maxDate }) {
  const today = todayLocalISO();
  const [view, setView] = useState(() => {
    const [y, m] = parts(start || today);
    return { y, m };
  });

  useEffect(() => {
    if (start) { const [y, m] = parts(start); setView({ y, m }); }
  }, [start]);

  const cells = useMemo(() => {
    const { y, m } = view;
    const first = new Date(y, m - 1, 1).getDay();
    const dim = daysInMonth(y, m);
    const prevY = m === 1 ? y - 1 : y, prevM = m === 1 ? 12 : m - 1;
    const prevDim = daysInMonth(prevY, prevM);
    const out = [];
    for (let i = first - 1; i >= 0; i--) out.push({ date: iso(prevY, prevM, prevDim - i), other: true });
    for (let d = 1; d <= dim; d++) out.push({ date: iso(y, m, d), other: false });
    const nextY = m === 12 ? y + 1 : y, nextM = m === 12 ? 1 : m + 1;
    let d = 1;
    while (out.length % 7 !== 0) out.push({ date: iso(nextY, nextM, d++), other: true });
    return out;
  }, [view]);

  const shift = (delta) => setView(({ y, m }) => {
    let nm = m + delta, ny = y;
    if (nm < 1) { nm = 12; ny -= 1; }
    if (nm > 12) { nm = 1; ny += 1; }
    return { y: ny, m: nm };
  });

  return (
    <div className="swm-cal">
      <div className="swm-cal-nav">
        <button type="button" onClick={() => shift(-1)} aria-label="Previous month">‹</button>
        <strong>{MONTHS[view.m - 1]} {view.y}</strong>
        <button type="button" onClick={() => shift(1)} aria-label="Next month">›</button>
      </div>
      <div className="swm-cal-grid">
        {DOW.map((d) => <div key={d} className="swm-dow">{d}</div>)}
        {cells.map(({ date, other }) => {
          const disabled = date < minDate || date > maxDate;
          const cls = ['swm-day'];
          if (other) cls.push('other');
          if (date === today) cls.push('today');
          if (start && end && date > start && date < end) cls.push('in-range');
          if (date === start) cls.push('range-start');
          if (date === end) cls.push('range-end');
          return (
            <button
              key={date}
              type="button"
              className={cls.join(' ')}
              disabled={disabled}
              title={date === end ? 'End date (locked: start + 1 month)' : `Set start date ${fmt(date)}`}
              onClick={() => onPickStart(date)}
            >
              {Number(date.slice(8))}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---- Modal ------------------------------------------------------------------
const SubscriptionWindowModal = ({ onClose }) => {
  const [businesses, setBusinesses] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);      // { business, history }
  const [startDate, setStartDate] = useState('');
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);            // { type: 'ok'|'err', text }
  const [serverToday, setServerToday] = useState(todayLocalISO());

  const endDate = startDate ? addOneMonth(startDate) : '';

  const { minDate, maxDate } = useMemo(() => startDateBounds(serverToday), [serverToday]);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await fetch(`${API_BASE_URL}/subscription-window/businesses`, { headers: authHeaders() });
      const data = await r.json();
      if (!r.ok || !data.success) throw new Error(data.error || `HTTP ${r.status}`);
      setBusinesses(data.businesses || []);
      if (data.today) setServerToday(data.today);
    } catch (e) {
      setMsg({ type: 'err', text: `Could not load businesses: ${e.message}` });
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadDetail = useCallback(async (businessId) => {
    setDetail(null); setMsg(null); setConfirming(false); setReason('');
    try {
      const r = await fetch(`${API_BASE_URL}/subscription-window/${businessId}`, { headers: authHeaders() });
      const data = await r.json();
      if (!r.ok || !data.success) throw new Error(data.error || `HTTP ${r.status}`);
      setDetail(data);
      // Default the picker to the existing start, else today.
      setStartDate(data.business.window.start || data.today || todayLocalISO());
    } catch (e) {
      setMsg({ type: 'err', text: `Could not load business: ${e.message}` });
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (selectedId) loadDetail(selectedId); }, [selectedId, loadDetail]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return businesses;
    return businesses.filter((b) =>
      String(b.businessId).includes(q)
      || (b.businessName || '').toLowerCase().includes(q)
      || (b.ownerEmail || '').toLowerCase().includes(q));
  }, [businesses, query]);

  const biz = detail?.business;
  const unchanged = biz && biz.window.start === startDate;

  // Edge-case warnings shown before the admin confirms.
  const warnings = useMemo(() => {
    if (!biz || !startDate) return [];
    const w = [];
    if (startDate > serverToday) w.push(`Start is in the future — the customer will be LOCKED OUT until ${fmt(startDate)}.`);
    if (endDate < serverToday) w.push(`This window has already ended (${fmt(endDate)}) — the customer will be locked immediately.`);
    if (!biz.paidSubscription && biz.isActive && biz.accessUntil) {
      const curEnd = String(new Date(biz.accessUntil).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }));
      if (endDate < curEnd) w.push(`New end (${fmt(endDate)}) is EARLIER than the customer's current access end (${fmt(curEnd)}) — this shortens their access.`);
    }
    if (biz.paidSubscription) w.push('Owner has a paid subscription flag — the window is saved but not enforced while that flag is TRUE.');
    return w;
  }, [biz, startDate, endDate, serverToday]);

  const save = async () => {
    if (!biz || !startDate) return;
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`${API_BASE_URL}/subscription-window/${biz.businessId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ startDate, reason })   // NOTE: no endDate — server derives it
      });
      const data = await r.json();
      if (!r.ok || !data.success) throw new Error(data.error || `HTTP ${r.status}`);
      setMsg({ type: 'ok', text: `${data.message}. The customer will see the new window on their next login / page refresh.` });
      setConfirming(false);
      await loadList();
      await loadDetail(biz.businessId);
    } catch (e) {
      setMsg({ type: 'err', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const clearWindow = async () => {
    if (!biz || !biz.window.end) return;
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Remove the fixed window for "${biz.businessName}"? Access will fall back to the 30-day trial rule from account creation.`)) return;
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`${API_BASE_URL}/subscription-window/${biz.businessId}`, {
        method: 'DELETE', headers: authHeaders(), body: JSON.stringify({ reason })
      });
      const data = await r.json();
      if (!r.ok || !data.success) throw new Error(data.error || `HTTP ${r.status}`);
      setMsg({ type: 'ok', text: data.message });
      await loadList();
      await loadDetail(biz.businessId);
    } catch (e) {
      setMsg({ type: 'err', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const statusPill = (b) => {
    if (b.paidSubscription) return <span className="swm-pill paid">paid</span>;
    return <span className={`swm-pill ${b.isActive ? 'active' : 'expired'}`}>{b.isActive ? 'active' : 'locked'}</span>;
  };

  return ReactDOM.createPortal(
    <div className="swm-overlay" onClick={onClose}>
      <div className="swm-box" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Subscription window">
        <div className="swm-header">
          <div>
            <h2>🗓️ Subscription Window</h2>
            <p>Pick a start date — access is granted for exactly one month from that day. INVEXIS admin only.</p>
          </div>
          <button className="swm-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="swm-body">
          {/* Left: business picker */}
          <div className="swm-list">
            <input
              className="swm-search"
              placeholder="Search business, owner email or ID…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="swm-list-items">
              {loadingList ? <div className="swm-empty">Loading businesses…</div>
                : filtered.length === 0 ? <div className="swm-empty">No businesses match.</div>
                : filtered.map((b) => (
                  <button
                    key={b.businessId}
                    className={`swm-biz ${selectedId === b.businessId ? 'active' : ''}`}
                    onClick={() => setSelectedId(b.businessId)}
                  >
                    <span className="swm-biz-name">#{b.businessId} · {b.businessName || 'Unnamed'}</span>
                    <span className="swm-biz-meta">
                      <span>{b.window.end ? `${fmt(b.window.start)} → ${fmt(b.window.end)}` : (b.paidSubscription ? 'Paid' : '30-day trial')}</span>
                      {statusPill(b)}
                    </span>
                  </button>
                ))}
            </div>
          </div>

          {/* Right: detail + calendar */}
          <div className="swm-detail">
            {!selectedId && <div className="swm-empty">Select a business on the left to view or move its window.</div>}
            {selectedId && !biz && !msg && <div className="swm-empty">Loading…</div>}

            {biz && (
              <>
                <div className="swm-current">
                  <div className="swm-stat"><label>Business</label><strong>#{biz.businessId} {biz.businessName}</strong><div className="swm-sub">{biz.ownerEmail || 'no owner email'}</div></div>
                  <div className="swm-stat"><label>Current window</label>
                    <strong>{biz.window.end ? `${fmt(biz.window.start)} → ${fmt(biz.window.end)}` : 'None (30-day trial rule)'}</strong>
                    {biz.window.updatedAt && <div className="swm-sub">set {fmtDateTime(biz.window.updatedAt)} by {biz.window.updatedBy || '—'}</div>}
                  </div>
                  <div className="swm-stat"><label>Status</label>
                    <strong>{biz.paidSubscription ? 'Paid subscription' : biz.isActive ? 'Active' : 'Locked (expired)'}</strong>
                    <div className="swm-sub">{biz.accessUntil ? `access until ${fmtDateTime(biz.accessUntil)}` : biz.paidSubscription ? 'window not enforced while paid' : ''}</div>
                  </div>
                </div>

                <div className="swm-section-title">New window — click the start date</div>
                <MonthCalendar start={startDate} end={endDate} onPickStart={(d) => { setStartDate(d); setConfirming(false); setMsg(null); }} minDate={minDate} maxDate={maxDate} />

                <div className="swm-preview">
                  <div>
                    <div className="swm-sub">Start (selected)</div>
                    <div className="swm-date">{fmt(startDate)}</div>
                  </div>
                  <div className="swm-arrow">→</div>
                  <div>
                    <div className="swm-sub">End <span className="swm-lock">🔒 locked · start + 1 month</span></div>
                    <div className="swm-date">{fmt(endDate)}</div>
                  </div>
                </div>

                <textarea
                  className="swm-reason"
                  placeholder="Reason (recommended) — e.g. 'Customer could not enter data 1–8 Sep due to POS outage'"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={500}
                />

                {warnings.length > 0 && !unchanged && (
                  <div className="swm-confirm">
                    {warnings.map((w, i) => <div key={i}>⚠️ {w}</div>)}
                  </div>
                )}

                {confirming && (
                  <div className="swm-confirm">
                    You are about to set <strong>#{biz.businessId} {biz.businessName}</strong> to
                    {' '}<strong>{fmt(startDate)} → {fmt(endDate)}</strong>.
                    {biz.window.end && <> This replaces the current window {fmt(biz.window.start)} → {fmt(biz.window.end)}.</>}
                    {' '}The change is saved to the database and logged with your email.
                  </div>
                )}

                <div className="swm-actions">
                  {!confirming ? (
                    <button className="swm-btn primary" disabled={!startDate || unchanged || saving} onClick={() => setConfirming(true)}>
                      {unchanged ? 'No change' : 'Review change'}
                    </button>
                  ) : (
                    <>
                      <button className="swm-btn primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Confirm & save'}</button>
                      <button className="swm-btn ghost" disabled={saving} onClick={() => setConfirming(false)}>Back</button>
                    </>
                  )}
                  {biz.window.end && (
                    <button className="swm-btn danger" disabled={saving} onClick={clearWindow}>Remove window</button>
                  )}
                </div>

                {msg && <div className={`swm-msg ${msg.type}`}>{msg.text}</div>}

                {detail.history?.length > 0 && (
                  <div className="swm-history">
                    <div className="swm-section-title">History</div>
                    <table>
                      <thead><tr><th>When</th><th>Action</th><th>From</th><th>To</th><th>By</th><th>Reason</th></tr></thead>
                      <tbody>
                        {detail.history.map((h) => (
                          <tr key={h.log_id}>
                            <td>{fmtDateTime(h.changed_at)}</td>
                            <td>{h.action}</td>
                            <td>{h.old_end ? `${fmt(h.old_start)} → ${fmt(h.old_end)}` : '—'}</td>
                            <td>{h.new_end ? `${fmt(h.new_start)} → ${fmt(h.new_end)}` : '—'}</td>
                            <td>{h.changed_by_email || '—'}</td>
                            <td>{h.reason || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
            {!biz && msg && <div className={`swm-msg ${msg.type}`}>{msg.text}</div>}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SubscriptionWindowModal;
